import axios, { AxiosError } from 'axios';

const SENSITIVE_KEY =
  /password|passwd|secret|authorization|recaptcha|access_token|refresh_token|warehouse_token|cookie|^(token|access|refresh|jwt)$/i;
const MAX_STRING_LENGTH = 500;

export type AxiosEventContext = {
  method: string;
  url: string;
  path: string;
  status?: number;
  statusText?: string;
  responseData?: unknown;
};

const truncate = (value: string): string => (value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value);

const sanitize = (value: unknown, depth = 0): unknown => {
  if (value == null || depth > 3) {
    return value;
  }

  if (typeof value === 'string') {
    return truncate(value);
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map(item => sanitize(item, depth + 1));
  }

  if (typeof value === 'object') {
    try {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, nested]) => {
        acc[key] = SENSITIVE_KEY.test(key) ? '[Filtered]' : sanitize(nested, depth + 1);
        return acc;
      }, {});
    } catch {
      return '[Unserializable]';
    }
  }

  return value;
};

export const redactSensitiveUrl = (raw: string): string => {
  try {
    const url = new URL(raw, typeof window === 'undefined' ? 'https://app.mojamatura.edu.pl' : window.location.origin);
    url.hash = '';
    url.searchParams.delete('access_token');
    url.searchParams.delete('token');
    url.pathname = url.pathname.replace(/\/password-reset\/[^/]+\/[^/]+/i, '/password-reset/[redacted]');
    return `${url.origin}${url.pathname}${url.search}`;
  } catch {
    return raw;
  }
};

export const redactSensitivePath = (pathname: string, search = ''): string => {
  try {
    const redacted = redactSensitiveUrl(`https://app.mojamatura.edu.pl${pathname}${search}`);
    const url = new URL(redacted);
    return `${url.pathname}${url.search}`;
  } catch {
    return pathname;
  }
};

const resolveAxiosUrl = (error: AxiosError): string => {
  const baseURL = error.config?.baseURL || '';
  const path = error.config?.url || '';

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (baseURL && path) {
    return `${baseURL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  try {
    if (error.config) {
      return axios.getUri(error.config);
    }
  } catch {
    // Fall through when getUri cannot parse the config.
  }

  return path || baseURL;
};

const resolvePath = (url: string): string => {
  try {
    return new URL(url, typeof window === 'undefined' ? 'https://app.mojamatura.edu.pl' : window.location.origin).pathname;
  } catch {
    return url;
  }
};

export const getAxiosEventContext = (error: unknown): AxiosEventContext | null => {
  try {
    if (!axios.isAxiosError(error)) {
      return null;
    }

    const url = resolveAxiosUrl(error);
    const method = error.config?.method?.toUpperCase() || 'GET';

    return {
      method,
      url,
      path: resolvePath(url),
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: sanitize(error.response?.data),
    };
  } catch {
    return null;
  }
};

type SentryEventLike = {
  fingerprint?: string[];
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  request?: { url?: string };
};

type SentryDropEventLike = {
  culprit?: string;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      stacktrace?: {
        frames?: Array<{ filename?: string; absPath?: string }>;
      };
    }>;
  };
};

const RECAPTCHA_SCRIPT = /gstatic\.com\/recaptcha|google\.com\/recaptcha|recaptcha\/releases|recaptcha__/i;
const NETWORK_FETCH_ERROR =
  /^(Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?)$/i;

const eventFrames = (event: SentryDropEventLike) => event.exception?.values?.[0]?.stacktrace?.frames || [];

const eventUrls = (event: SentryDropEventLike): string[] => {
  const urls = eventFrames(event).map(frame => `${frame.filename || ''} ${frame.absPath || ''}`);
  if (event.culprit) {
    urls.push(event.culprit);
  }
  return urls;
};

const hasAppStackFrame = (event: SentryDropEventLike): boolean =>
  eventFrames(event).some(frame => {
    const path = [frame.filename, frame.absPath].filter(Boolean).join('\n');
    if (/node_modules/.test(path)) {
      return false;
    }
    return /(?:^|\/)src\//.test(path) || /\.(tsx|ts|jsx)(?:\?|$)/m.test(path);
  });

/** Google reCAPTCHA throws in a setTimeout after the widget is unmounted (typical SPA login). */
export const shouldDropSentryEvent = (event: SentryDropEventLike): boolean => {
  try {
    if (eventUrls(event).some(url => RECAPTCHA_SCRIPT.test(url))) {
      return true;
    }

    const exception = event.exception?.values?.[0];
    const message = exception?.value || '';
    if (NETWORK_FETCH_ERROR.test(message) && !hasAppStackFrame(event)) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

export const applyAxiosContextToEvent = (event: SentryEventLike, error: unknown): SentryEventLike => {
  try {
    if (event.request?.url) {
      event.request.url = redactSensitiveUrl(event.request.url);
    }

    const axiosContext = getAxiosEventContext(error);
    if (!axiosContext) {
      return event;
    }

    const statusKey = axiosContext.status != null ? String(axiosContext.status) : 'network';

    event.fingerprint = ['axios', axiosContext.method, axiosContext.path, statusKey];
    event.tags = {
      ...event.tags,
      'http.method': axiosContext.method,
      'http.status_code': statusKey,
      'http.path': axiosContext.path,
    };
    event.extra = {
      ...event.extra,
      axios: axiosContext,
    };
    event.contexts = {
      ...event.contexts,
      api: {
        method: axiosContext.method,
        url: axiosContext.url,
        path: axiosContext.path,
        status: axiosContext.status ?? 'network',
        response: axiosContext.responseData ?? null,
      },
    };
  } catch {
    // Never let Sentry enrichment break event delivery or the app.
  }

  return event;
};
